
import { Component, Vue, Watch } from 'vue-property-decorator'

import BacLoggedInHeader from '../templates/BacLoggedInHeader.vue'

import { Rally } from '../lib/models/Rally'
import { RallyRound } from '../lib/models/RallyRound'
import { Discount } from '../lib/models/Discount'
import { User } from '../lib/models/User'
import { Country } from '../lib/models/Country'
import { CustomerRegister } from '../lib/models/CustomerRegister'

import BacInput from '@/components/BacInput.vue'
import BacButton from '@/components/BacButton.vue'
import BacAlert from '@/components/BacAlert.vue'
import { Http } from '@/lib/utils/Http'
import { FixedValueDiscount } from '../lib/models/FixedValueDiscount'
import { DateUtils } from '../lib/utils/DateUtils'

import RallyRegisterAddress from '../components/rally-register/RallyRegisterAddress.vue'
import RallyRegisterOtherData from '../components/rally-register/RallyRegisterOtherData.vue'

@Component({
  components: {
    BacLoggedInHeader,
    BacInput,
    BacButton,
    BacAlert,
    RallyRegisterAddress,
    RallyRegisterOtherData
  }
})
export default class RallyRegister extends Vue {
  ralliesOpenForRegistration: Rally[] = [];
  chosenRally: Rally | null = null;
  chosenRound: RallyRound | null = null;
  chosenVehicle: string | null = null;
  chosenTeamSize: number | null = null;
  discounts: Discount[] = [];

  fixedValueDiscounts: FixedValueDiscount[] = [];

  DateUtils = DateUtils;

  @Watch('chosenRally')
  public onChosenRallyChange () {
    this.chosenRound = null
    if (this.chosenRallyRoundsSortedByDate.length === 1) {
      this.chosenRound = this.chosenRallyRoundsSortedByDate[0]
    }
  }

  @Watch('chosenRound')
  public onChosenRallyRoundChange () {
    this.chosenVehicle = null
    // if only one vehicle type available, set it as default
    if (this.chosenRound) {
      // count how many vehicle types are available in this round
      let numberOfVehicleTypes = 0
      let availableVehicleType = ''
      for (let i = 1; i <= 3; i++) {
        if ((this.chosenRound as any)[`max_teams_${i}`]) {
          numberOfVehicleTypes++
          switch (i) {
            case 1:
              availableVehicleType = 'car'
              break
            case 2:
              availableVehicleType = 'bike'
              break
            case 3:
              availableVehicleType = 'boat'
              break
          }
        }
      }
      if (numberOfVehicleTypes === 1) {
        this.chosenVehicle = availableVehicleType
      }
    }
  }

  get chosenRallyRoundsSortedByDate () {
    return (this.chosenRally as any).rallyRounds.sort((a: any, b: any) => {
      return (
        new Date(a.date_begin).getTime() - new Date(b.date_begin).getTime()
      )
    })
  }

  user: User = new User();

  waitlist = false;

  newsletter = false;
  dsgvo = false;
  agb = false;

  readRegistrationInfo = false;

  // don't submit, a request is currently pending
  waitingForRegister = false;

  validateErrors: string[] = [];

  mounted () {
    this.checkLogin()
    this.loadRalliesOpenForRegistration()
    this.loadDiscounts()
    this.loadUser()
    this.sendFacebookInitiateCheckoutEvent()
    this.loadFixedValueDiscounts()
  }

  private sendFacebookInitiateCheckoutEvent () {
    (window as any).fbq('track', 'InitiateCheckout')
  }

  get isSoldOut () {
    switch (this.chosenVehicle) {
      case 'car':
        return (this.chosenRound as any).availableCars < 1
      case 'bike':
        return (this.chosenRound as any).availableBikes < 1
      case 'boat':
        return (this.chosenRound as any).availableBoats < 1
    }
  }

  get fixedValueDiscountsSum (): number {
    let sum = 0
    this.fixedValueDiscounts.forEach((discount) => {
      if (discount.restValue) {
        sum += discount.restValue
      }
    })
    return sum
  }

  get submitButtonText () {
    return this.waitlist || this.isSoldOut
      ? 'Auf die Warteliste'
      : 'Kostenpflichtig zum Rodeo anmelden'
  }

  private async checkLogin () {
    const res = await Http.request('/api/auth/check-login')
    if (res.status !== 200) {
      window.location.replace('/teamarea/login')
    }
  }

  async submit () {
    if (this.waitingForRegister) {
      return
    }
    this.validateErrors = this.validateData()
    if (this.validateErrors.length) {
      return
    }
    if (
      !this.chosenRally ||
      !this.chosenRound ||
      !this.user ||
      !this.user.countryId
    ) {
      return
    }
    const userCountry = this.user.country
    const customerRegister = new CustomerRegister()
    customerRegister.rallyId = this.chosenRally.id
    customerRegister.rallyRoundId = (this.chosenRound as any).rally_round_id
    customerRegister.teamType = this.teamType
    customerRegister.teamSize = this.chosenTeamSize
    customerRegister.phoneCountrynumber = this.user.telephone
    customerRegister.phoneFirstnumber = this.user.telephone
    customerRegister.phoneLastnumber = this.user.telephone
    customerRegister.street = `${this.user.street}`
    if (this.user.streetnumber) {
      customerRegister.street += ` ${this.user.streetnumber}`
    }
    customerRegister.zip = this.user.zip
    customerRegister.city = this.user.city
    customerRegister.countryId = this.user.countryId
    customerRegister.tshirtsize = this.user.tShirtSize
    customerRegister.price = this.price
    customerRegister.note = this.user.annotiation
    customerRegister.newsletter = this.newsletter
    customerRegister.gender = this.user.sex;

    // TODO: as any can be removed, but compile error
    (customerRegister.waitlist as any) = this.waitlist || this.isSoldOut;
    (customerRegister as any).customerReferrer = this.user.customerReferrer

    this.waitingForRegister = true
    const res = await Http.request('/api/customer-register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(customerRegister)
    })
    this.waitingForRegister = false;
    (window as any).fbq('track', 'CompleteRegistration')

    if (res.status === 410) {
      this.validateErrors = [
        'Leider sind alle verfügbaren Anmeldungen für dieses Rally Startdatum bereits vergriffen, versuche es mit einem anderen Startdatum oder drücke den Button nochmal um auf die Warteliste zu kommen!'
      ]
      this.waitlist = true
    } else if (res.status === 500) {
      this.validateErrors = [
        'Es ist ein Fehler beim Abschicken deiner Anmeldung aufgetreten. Bitte versuche es etwas später erneut, sollte dies nicht funktionieren melde dich bitte bei uns unter hello@backroadclub.com'
      ]
    } else if (res.status === 200) {
      const resData = await res.json()
      this.$router.push({ path: resData.url })
    }
  }

  private validateData (): string[] {
    const errors: string[] = []
    if (!this.chosenRally) {
      errors.push('Bitte wähle eine Rodeo aus!')
    }
    if (!this.chosenRound) {
      errors.push('Bitte wähle ein Rodeo Startdatum aus!')
    }
    if (!this.chosenVehicle) {
      errors.push('Bitte wähle dein Rally Fahrzeug aus!')
    }
    if (!this.chosenTeamSize) {
      errors.push('Bitte wähle deine Teamgröße aus!')
    }
    if (!this.user.street) {
      errors.push('Bitte fülle deine Straße aus!')
    }
    if (!this.user.tShirtSize) {
      errors.push('Bitte wähle deine T-Shirt Größe aus!')
    }
    if (!this.user.zip) {
      errors.push('Bitte fülle deine Postleitzahl aus!')
    }
    if (!this.user.city) {
      errors.push('Bitte fülle deinen Ort aus!')
    }
    if (!this.user.countryId) {
      errors.push('Bitte fülle dein Land aus!')
    }
    if (!this.user.telephone) {
      errors.push('Bitte fülle deine Telefonnummer vollständig aus!')
    }
    if (!this.dsgvo) {
      errors.push('Bitte bestätige unsere Datenschutzbestimmungen!')
    }
    if (!this.agb) {
      errors.push('Bitte bestätige unsere AGB!')
    }
    if (!this.user.customerReferrer) {
      errors.push('Bitte fülle aus woher du uns kennst!')
    }
    if (
      (this.chosenRound as any).registration_info_required &&
      !this.readRegistrationInfo
    ) {
      errors.push(
        'Bitte bestätige das du die Rodeo Informationen (unter Feld "Teamgröße") gelesen hast!'
      )
    }
    return errors
  }

  async loadFixedValueDiscounts () {
    this.fixedValueDiscounts = await FixedValueDiscount.getForLoggedInUser()
  }

  private async loadUser () {
    const userData = await (await Http.request('/api/user/logged-in')).json()
    this.user = new User()
    this.user.id = userData.customer_id
    this.user.firstname = userData.firstname
    this.user.lastname = userData.lastname
    this.user.email = userData.email
    this.user.street = userData.street
    this.user.zip = userData.zip
    this.user.city = userData.city
    this.user.telephone = userData.telephone
    this.user.customerReferrer = userData.customer_referrer
    this.user.tShirtSize = userData.t_shirt_size;
    (this.user as any).countryId = userData.country_id
  }

  get discountForChosenRally () {
    return this.discounts.find((discount: Discount) => {
      if (!this.chosenRally || !this.chosenRally.id) {
        return false
      }
      if (discount.rallyId === this.chosenRally.id || discount.rallyId === 0) {
        if (!discount.rallyRoundId) {
          return true
        } else {
          if (
            discount.rallyRoundId === (this.chosenRound as any).rally_round_id
          ) {
            return true
          }
        }
      }
    })
  }

  private async loadDiscounts () {
    const resData = await (
      await Http.request('/api/discount/current-user')
    ).json()

    resData.forEach((discount: any) => {
      const newDiscount = new Discount()
      newDiscount.id = discount.id
      newDiscount.rallyId = discount.rally_id
      newDiscount.percent = discount.percent
      newDiscount.name = discount.name
      newDiscount.rallyRoundId = discount.rally_round_id
        ? discount.rally_round_id
        : 0
      this.discounts.push(newDiscount)
    })
  }

  get priceWithoutFixedValueDiscounts () {
    let priceString: string
    if (!this.chosenRound || !this.chosenTeamSize) {
      return 0
    }
    switch (this.chosenVehicle) {
      case 'car':
        if (!this.chosenRound.team_types_1) {
          return 0
        }
        priceString = this.chosenRound.team_types_1
        break
      case 'bike':
        if (!this.chosenRound.team_types_2) {
          return 0
        }
        priceString = this.chosenRound.team_types_2
        break
      case 'boat':
        if (!this.chosenRound.team_types_3) {
          return 0
        }
        priceString = this.chosenRound.team_types_3
        break
      default:
        if (!this.chosenRound.team_types_1) {
          return 0
        }
        priceString = this.chosenRound.team_types_1
    }

    let price = 0
    priceString.split(';').forEach((priceTag) => {
      const priceTagSplit = priceTag.split('=')
      if (!this.chosenTeamSize) {
        return 0
      }
      if (priceTagSplit[0] === this.chosenTeamSize.toString()) {
        price = parseFloat(priceTagSplit[1])
      }
    })

    // Taxes
    price = Math.round(price * 120) / 100

    // Discount
    if (this.discountForChosenRally && this.discountForChosenRally.percent) {
      price = price - (price * this.discountForChosenRally.percent) / 100
    }
    return price
  }

  get price (): number {
    let price = this.priceWithoutFixedValueDiscounts

    // Fixed Value Discount
    price -= this.fixedValueDiscountsSum

    if (price < 0) {
      price = 0
    }
    return Math.round(price * 100) / 100
  }

  get teamType (): number {
    if (!this.chosenVehicle) {
      return -1
    }
    switch (this.chosenVehicle) {
      case 'car':
        return 1
      case 'bike':
        return 2
      case 'boat':
        return 3
      default:
        return 1
    }
  }

  get vehicleName () {
    switch (this.chosenVehicle) {
      case 'car':
        return 'Auto'
      case 'bike':
        return 'Bike'
      case 'boat':
        return 'Boot'
    }
  }

  get teamTypes (): string[] {
    if (!this.chosenRound) {
      return []
    }
    switch (this.chosenVehicle) {
      case 'car':
        if (!this.chosenRound.team_types_1) {
          return []
        }
        return this.chosenRound.team_types_1.replace(/;\s*$/, '').split(';')
      case 'bike':
        if (!this.chosenRound.team_types_2) {
          return []
        }
        return this.chosenRound.team_types_2.replace(/;\s*$/, '').split(';')
      case 'boat':
        if (!this.chosenRound.team_types_3) {
          return []
        }
        return this.chosenRound.team_types_3.replace(/;\s*$/, '').split(';')
      default:
        return []
    }
  }

  private async loadRalliesOpenForRegistration () {
    const ralliesJson = await (
      await Http.request('/api/rally/open-registration')
    ).json()

    ralliesJson.forEach((rally: any) => {
      const newRally = new Rally()
      newRally.id = rally.rally_id
      newRally.name = rally.name;
      (newRally as any).rallyRounds = rally.rally_rounds
      this.ralliesOpenForRegistration.push(newRally)
    })
  }
}
